/**
 * Notification audio pack — load and resolve the grok-voice-v1 catalog.
 *
 * Playback and settings import ids from the manifest. Components must not
 * hard-code scattered file paths; resolve through this module only.
 */

export const NOTIFICATION_PACK_ID = 'grok-voice-v1';
export const NOTIFICATION_PACK_BASE = `/notification-audio/${NOTIFICATION_PACK_ID}`;
export const NOTIFICATION_MANIFEST_URL = `${NOTIFICATION_PACK_BASE}/manifest.json`;

export type NotificationAssetKind = 'common' | 'number' | 'phrase' | 'sfx';
export type NotificationAssetStatus = 'ready' | 'pending-clean-export' | string;

export interface NotificationAssetFiles {
  readonly masterWav: string;
  readonly webMp3: string;
}

export interface NotificationAsset {
  readonly id: string;
  readonly meaning: string;
  readonly kind: NotificationAssetKind | string;
  readonly files: NotificationAssetFiles;
  readonly durationSec: number | null;
  readonly voice: string | null;
  readonly version: number;
  readonly status: NotificationAssetStatus;
  readonly statusNote?: string;
}

export interface NotificationJoinTest {
  readonly id: string;
  readonly tokens: readonly string[];
}

export interface NotificationPackManifest {
  readonly packId: string;
  readonly version: number;
  readonly voice: {
    readonly id: string;
    readonly label: string;
    readonly source: string;
    readonly fallbackNote?: string;
    readonly interim?: boolean;
    readonly interimTool?: string;
  };
  readonly sampleRate: number;
  readonly channels: number;
  readonly loudnessTargetLUFS: number;
  readonly formats: readonly string[];
  readonly publicPathHint?: string;
  readonly concatModel?: string;
  readonly status?: string;
  readonly joinTests: readonly NotificationJoinTest[];
  readonly assets: readonly NotificationAsset[];
}

export interface ResolvedNotificationAsset {
  readonly id: string;
  readonly meaning: string;
  readonly kind: string;
  readonly status: string;
  readonly durationSec: number | null;
  /** Browser-ready absolute URL (MP3). */
  readonly url: string;
  readonly relativeWebMp3: string;
}

const NUMBER_WORDS = [
  'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
  'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen',
  'eighteen', 'nineteen', 'twenty',
] as const;

/** Worker number 1–20 → token id `token-one` … `token-twenty`. Null outside range. */
export function workerNumberTokenId(workerNumber: number): string | null {
  if (!Number.isInteger(workerNumber) || workerNumber < 1 || workerNumber > 20) return null;
  return `token-${NUMBER_WORDS[workerNumber - 1]}`;
}

/** Absolute public URL for a path relative to the pack root. */
export function packAssetUrl(relativePath: string, base: string = NOTIFICATION_PACK_BASE): string {
  const cleaned = relativePath.replace(/^\/+/, '');
  return `${base.replace(/\/+$/, '')}/${cleaned}`;
}

/** Index ready assets by id; skip non-ready so callers fall back safely. */
export function indexReadyAssets(
  manifest: NotificationPackManifest,
  base: string = NOTIFICATION_PACK_BASE,
): ReadonlyMap<string, ResolvedNotificationAsset> {
  const map = new Map<string, ResolvedNotificationAsset>();
  for (const asset of manifest.assets) {
    if (asset.status !== 'ready') continue;
    const relative = asset.files.webMp3;
    map.set(asset.id, {
      id: asset.id,
      meaning: asset.meaning,
      kind: asset.kind,
      status: asset.status,
      durationSec: asset.durationSec,
      relativeWebMp3: relative,
      url: packAssetUrl(relative, base),
    });
  }
  return map;
}

/** Resolve a token id to a ready asset, or null when missing/not ready. */
export function resolveAsset(
  assets: ReadonlyMap<string, ResolvedNotificationAsset>,
  tokenId: string,
): ResolvedNotificationAsset | null {
  return assets.get(tokenId) ?? null;
}

/**
 * Resolve an ordered list of token ids. Missing tokens are omitted (caller may
 * decide to fall back to SFX). Never throws.
 */
export function resolveTokenSequence(
  assets: ReadonlyMap<string, ResolvedNotificationAsset>,
  tokenIds: readonly string[],
): readonly ResolvedNotificationAsset[] {
  const out: ResolvedNotificationAsset[] = [];
  for (const id of tokenIds) {
    const asset = resolveAsset(assets, id);
    if (asset !== null) out.push(asset);
  }
  return out;
}

/** Pure join-test helper: map a joinTests entry to resolved assets. */
export function resolveJoinTest(
  manifest: NotificationPackManifest,
  joinId: string,
  base: string = NOTIFICATION_PACK_BASE,
): readonly ResolvedNotificationAsset[] | null {
  const join = manifest.joinTests.find((row) => row.id === joinId);
  if (!join) return null;
  return resolveTokenSequence(indexReadyAssets(manifest, base), join.tokens);
}

/** Minimal structural check for a fetched manifest object. */
export function isNotificationPackManifest(value: unknown): value is NotificationPackManifest {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const row = value as Record<string, unknown>;
  return typeof row.packId === 'string'
    && typeof row.version === 'number'
    && Array.isArray(row.assets)
    && Array.isArray(row.joinTests);
}

/**
 * Fetch + parse the pack manifest. Returns null on network/parse failure so
 * the player can stay silent without breaking the desk.
 */
export async function loadNotificationPackManifest(
  fetchImpl: typeof fetch = fetch,
  url: string = NOTIFICATION_MANIFEST_URL,
): Promise<NotificationPackManifest | null> {
  try {
    const response = await fetchImpl(url, { credentials: 'same-origin' });
    if (!response.ok) return null;
    const json: unknown = await response.json();
    return isNotificationPackManifest(json) ? json : null;
  } catch {
    return null;
  }
}
